/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type FeatureFlagDetails = {
  enabled: boolean;
  content?: string | null;
};

/** A single boundary point as supplied by the backend feature flag */
export type BoundaryPoint = {
  lon: number;
  lat: number;
};

/** Extended flag details for the Iran Conflict panel, which carries boundary polygons */
export type IranConflictPanelFlagDetails = FeatureFlagDetails & {
  badgeContent?: string | null;
  /** Polygon used for spatial filtering (list views, map layers). */
  contentBoundary?: BoundaryPoint[] | null;
  /** Polygon used for the initial flyTo on activation. Falls back to contentBoundary when absent. */
  initialViewBoundary?: BoundaryPoint[] | null;
};

export type FeatureFlags = {
  canAccessFeatureMyFleet: FeatureFlagDetails;
  canAccessFeatureProReports: FeatureFlagDetails;
  canAccessFeatureIrisVesselsLink: FeatureFlagDetails;
  canAccessFeatureProFeatureBadge: FeatureFlagDetails;
  canAccessFeatureIranConflictPanel: IranConflictPanelFlagDetails;
  canAccessFeatureAisApiHistory: FeatureFlagDetails;
  canAccessFeatureAisApiNearbyVessels: FeatureFlagDetails;
};

export interface FeatureFlagsState {
  flags: FeatureFlags;
}

export const INITIAL_FEATURE_FLAGS_STATE: FeatureFlagsState = {
  flags: {
    canAccessFeatureMyFleet: { enabled: false },
    canAccessFeatureProReports: { enabled: false },
    canAccessFeatureIrisVesselsLink: { enabled: false },
    canAccessFeatureProFeatureBadge: { enabled: false, content: null },
    canAccessFeatureIranConflictPanel: {
      enabled: false,
      badgeContent: null,
      contentBoundary: null,
      initialViewBoundary: null,
    },
    canAccessFeatureAisApiHistory: { enabled: false },
    canAccessFeatureAisApiNearbyVessels: { enabled: false },
  },
};

const FeatureFlagsSlice = createSlice({
  name: 'featureFlags',
  initialState: INITIAL_FEATURE_FLAGS_STATE,
  reducers: {
    setFeatureFlags: (state, action: PayloadAction<FeatureFlags>) => {
      state.flags = {
        ...state.flags,
        ...action.payload,
      };
    },
  },
});

export const { setFeatureFlags } = FeatureFlagsSlice.actions;

export default FeatureFlagsSlice.reducer;
